import { MailCheck, RefreshCw } from "lucide-react";

import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

export function EmailVerificationStep({
  email,
  error,
  isChecking = false,
  isResending = false,
  onResend,
}: {
  email?: string;
  error?: string | null;
  isChecking?: boolean;
  isResending?: boolean;
  onResend?: () => void;
}) {
  const displayEmail = email?.trim() || "your email address";

  return (
    <div
      aria-busy={isChecking || isResending}
      className="flex w-full max-w-[390px] flex-col items-center rounded-[18px] border border-border bg-background px-7 py-8 text-center shadow-[0_18px_50px_rgba(20,23,26,0.10)]"
    >
      <div className="flex size-[68px] items-center justify-center rounded-full bg-primary/10 text-primary">
        {isChecking ? (
          <Spinner size="lg" />
        ) : (
          <MailCheck aria-hidden="true" className="size-8" />
        )}
      </div>

      <h2 className="mt-5 text-xl font-semibold tracking-[-0.4px] text-foreground">
        Waiting for email verification
      </h2>
      <p className="mt-2 max-w-[300px] text-sm leading-[1.55] text-muted-foreground">
        We sent a verification link to{" "}
        <span className="font-medium text-foreground">{displayEmail}</span>.
        Keep this page open, then use the link in your inbox to continue.
      </p>

      {error ? (
        <p
          aria-live="polite"
          className="mt-4 max-w-[300px] rounded-lg border border-destructive/25 bg-destructive/10 px-3 py-2 text-sm leading-[1.45] text-destructive"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      <Button
        className="mt-5 h-[38px] rounded-[9px] text-[13px] font-semibold"
        disabled={isResending}
        onClick={onResend}
        type="button"
        variant="outline"
      >
        {isResending ? (
          <Spinner className="text-muted-foreground" size="sm" />
        ) : (
          <RefreshCw aria-hidden="true" className="size-4" />
        )}
        Resend verification email
      </Button>

      <p className="mt-4 max-w-[300px] font-mono text-[10.5px] leading-[1.5] text-muted-foreground">
        The link expires after one hour. You can request a new email if it
        expired or did not arrive.
      </p>
    </div>
  );
}
